import type { I18n } from '@lingui/core';
import { z } from 'zod';

import type { EnumMap, StatusIconName, StatusMap, StatusTone } from './status';
import { resolvePatternText, type PatternText } from './text';

/** Um valor de enum: rótulo traduzido (U33), tom e ícone opcionais (U19). */
export interface EnumEntry {
  readonly label: PatternText;
  readonly tone?: StatusTone;
  readonly icon?: StatusIconName;
  /** Existe no backend, mas a tela não oferece como opção (ex.: `None = 0`, "Não definido"). */
  readonly hidden?: boolean;
}

export interface DefineEnumOptions {
  /**
   * Ordem das opções: a da declaração (padrão), alfabética pelo rótulo traduzido (listas de status do
   * legado) ou explícita. Chave numérica (`'3'`, `'0'`) o JS reordena: passe a ordem do legado aqui.
   */
  readonly order?: 'declared' | 'label' | readonly string[];
  /** O fio manda número (Newtonsoft sem `StringEnumConverter`): `toWire('2')` devolve `2`. */
  readonly numeric?: boolean;
}

export interface EnumDef<V extends string = string> {
  readonly values: readonly V[];
  readonly entries: Readonly<Record<V, EnumEntry>>;
  /** `z.enum` dos valores técnicos (URL, formulário). */
  readonly schema: z.ZodType<V>;
  readonly numeric: boolean;
  has(value: unknown): value is V;
  entry(value: unknown): EnumEntry | undefined;
  /** Rótulo pelo valor técnico; fora do mapa, o `fallback` (o `*Display` da API) ou o próprio valor. */
  label(i18n: I18n, value: unknown, fallback?: string | null): string | undefined;
  /** Opções de `Select`/filtro, sem as `hidden`. */
  options(i18n: I18n, only?: readonly V[]): { value: V; label: string }[];
  /** Mapa para `col.enum` (só rótulo). */
  readonly labels: EnumMap;
  /** Mapa para `col.status` (tom `neutral` quando o valor não declara). */
  readonly statuses: StatusMap;
  /** Valor técnico → fio (número quando `numeric`). */
  toWire(value: V): string | number;
  /** Fio → valor técnico (aceita número). `undefined` fora do mapa. */
  fromWire(value: unknown): V | undefined;
}

const keyOf = (value: unknown): string | undefined => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : undefined);

/**
 * Enum do backend declarado uma vez (P22): valor técnico → rótulo traduzido, tom e ícone. Serve ao
 * `Select`, ao filtro, à coluna (`labels`/`statuses`) e ao mapper (`toWire`/`fromWire`).
 */
export function defineEnum<const E extends Readonly<Record<string, EnumEntry>>>(entries: E, options: DefineEnumOptions = {}): EnumDef<Extract<keyof E, string>> {
  type V = Extract<keyof E, string>;
  const declared = Object.keys(entries) as V[];
  const values = Array.isArray(options.order) ? [...(options.order as readonly V[]).filter((value) => declared.includes(value)), ...declared.filter((value) => !(options.order as readonly string[]).includes(value))] : declared;
  if (values.length === 0) throw new Error('defineEnum: enum vazio');
  const has = (value: unknown): value is V => {
    const key = keyOf(value);
    return key !== undefined && Object.hasOwn(entries, key);
  };
  const entry = (value: unknown) => {
    const key = keyOf(value);
    return key !== undefined && Object.hasOwn(entries, key) ? entries[key as V] : undefined;
  };
  const labels: Record<string, PatternText> = {};
  const statuses: Record<string, StatusMap[string]> = {};
  const at = (value: V): EnumEntry => entries[value] as EnumEntry;
  for (const value of values) {
    const item = at(value);
    labels[value] = item.label;
    statuses[value] = { label: item.label, tone: item.tone ?? 'neutral', ...(item.icon ? { icon: item.icon } : {}) };
  }
  return {
    values,
    entries,
    schema: z.enum(values as [V, ...V[]]),
    numeric: options.numeric === true,
    has,
    entry,
    label: (i18n, value, fallback) => {
      const item = entry(value);
      if (item) return resolvePatternText(i18n, item.label);
      if (fallback) return fallback;
      return keyOf(value);
    },
    options: (i18n, only) => {
      const list = values
        .filter((value) => at(value).hidden !== true && (!only || only.includes(value)))
        .map((value) => ({ value, label: resolvePatternText(i18n, at(value).label) }));
      return options.order === 'label' ? list.sort((a, b) => a.label.localeCompare(b.label, i18n.locale)) : list;
    },
    labels,
    statuses,
    toWire: (value) => (options.numeric ? Number(value) : value),
    fromWire: (value) => (has(value) ? (keyOf(value) as V) : undefined),
  };
}

/** Opção fixa de escolha, quando não há `defineEnum` (sim/não, lista curta do legado). */
export interface ChoiceOption {
  readonly value: string;
  readonly label: PatternText;
}

/** Um enum declarado ou uma lista de opções fixas. */
export type EnumLike = EnumDef | readonly ChoiceOption[];

/** É um `defineEnum` (e não uma lista de opções)? */
export const isEnumDef = (options: EnumLike): options is EnumDef => !Array.isArray(options);

/** Os valores técnicos de um enum ou de uma lista. */
export const enumValues = (options: EnumLike): readonly string[] => (isEnumDef(options) ? options.values : options.map((option) => option.value));

/** Rótulo de uma opção pelo valor técnico (U33). */
export function choiceLabel(i18n: I18n, options: EnumLike, value: string): string {
  if (isEnumDef(options)) return options.label(i18n, value) ?? value;
  const option = options.find((candidate) => candidate.value === value);
  return option ? resolvePatternText(i18n, option.label) : value;
}

/** Opções no idioma ativo, com o rótulo em texto (serve ao `Select`, aos chips e às colunas). */
export function choiceOptions(i18n: I18n, options: EnumLike): { value: string; label: string }[] {
  if (isEnumDef(options)) return options.options(i18n).map((option) => ({ value: option.value, label: option.label }));
  return options.map((option) => ({ value: option.value, label: resolvePatternText(i18n, option.label) }));
}

/** Mapa valor → rótulo para `col.enum`. */
export const enumLabels = (options: EnumLike): EnumMap => (isEnumDef(options) ? options.labels : Object.fromEntries(options.map((option) => [option.value, option.label])));
